import { useState, useCallback, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, User, AlertTriangle } from 'lucide-react';
import { ProxyUser } from '@/types/proxy';
import { ProxyAPI } from '@/utils/proxyApi';
import { useProxy } from '@/contexts/ProxyContext';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

interface ProxyUserSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProxyUserSearchModal({ isOpen, onClose }: ProxyUserSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProxyUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ProxyUser | null>(null);
  const [reason, setReason] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const { startProxySession, isStarting } = useProxy();

  // Debounced search
  useEffect(() => {
    logger.debug('🎯 Search effect triggered with query:', searchQuery, 'length:', searchQuery.length);
    
    const timeoutId = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        logger.debug('🎯 Starting search for:', searchQuery);
        setIsSearching(true);
        setSearchError(null);
        
        try {
          logger.debug('🎯 Calling ProxyAPI.searchUsers with query:', searchQuery);
          const results = await ProxyAPI.searchUsers(searchQuery);
          logger.debug('🎯 Search completed, results:', results);
          setSearchResults(results);
        } catch (error) {
          Sentry.captureException(error, {
            tags: { context: 'search_error', component: 'ProxyUserSearchModal' },
            extra: { message: 'Search failed', searchQuery }
          });
          setSearchError('Failed to search users');
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        logger.debug('🎯 Query too short, clearing results');
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleUserSelect = useCallback(async (user: ProxyUser) => {
    // Validate that user can be proxied
    try {
      const canProxy = await ProxyAPI.canProxyUser(user.id);
      if (!canProxy) {
        toast.error('You do not have permission to proxy this user');
        return;
      }
      setSelectedUser(user);
    } catch (error) {
      toast.error('Failed to validate proxy permission');
    }
  }, []);

  const handleStartProxy = useCallback(async () => {
    if (!selectedUser) return;

    try {
      await startProxySession({
        target_user_id: selectedUser.id,
        reason: reason.trim() || 'Administrative access',
        duration_minutes: 25
      });
      
      // Reset form and close modal
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUser(null);
      setReason('');
      onClose();
    } catch (error) {
      // Error is already handled in context with toast
    }
  }, [selectedUser, reason, startProxySession, onClose]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedUser(null);
    setReason('');
    setSearchError(null);
    onClose();
  }, [onClose]);

  const getRoleColor = (role: string): string => {
    switch (role) {
      case 'super_admin': return 'bg-destructive/10 text-destructive';
      case 'administrator': return 'bg-primary/10 text-primary';
      case 'business_owner': return 'bg-accent/10 text-accent';
      case 'business_admin': return 'bg-accent/10 text-accent';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Start Proxy Session
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-6">
          {!selectedUser ? (
            <>
              {/* Search Section */}
              <div className="space-y-2">
                <Label htmlFor="search">Search Users</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder="Search by name or email (minimum 2 characters)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Search Results */}
              <div className="flex-1 overflow-hidden">
                {isSearching && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2">Searching users...</span>
                  </div>
                )}

                {searchError && (
                  <div className="flex items-center gap-2 text-destructive py-4">
                    <AlertTriangle className="h-4 w-4" />
                    <span>{searchError}</span>
                  </div>
                )}

                {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && !searchError && (
                  <div className="text-center py-8 text-muted-foreground">
                    No users found matching your search.
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {searchResults.map((user) => (
                      <div
                        key={user.id}
                        className="p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                        onClick={() => handleUserSelect(user)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">
                                {user.full_name || user.email}
                              </p>
                              <Badge 
                                variant="outline" 
                                className={getRoleColor(user.role)}
                              >
                                {user.role.replace('_', ' ')}
                              </Badge>
                            </div>
                            {user.full_name && (
                              <p className="text-sm text-muted-foreground truncate">
                                {user.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Selected User Confirmation */}
              <div className="space-y-4">
                <div className="p-4 border rounded-lg bg-accent/50">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {selectedUser.full_name || selectedUser.email}
                        </p>
                        <Badge 
                          variant="outline" 
                          className={getRoleColor(selectedUser.role)}
                        >
                          {selectedUser.role.replace('_', ' ')}
                        </Badge>
                      </div>
                      {selectedUser.full_name && (
                        <p className="text-sm text-muted-foreground">
                          {selectedUser.email}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUser(null)}
                    >
                      Change
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason for Proxy Session</Label>
                  <Textarea
                    id="reason"
                    placeholder="Enter the reason for accessing this user's account..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    This reason will be logged for audit purposes.
                  </p>
                </div>

                <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-warning mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-warning">
                        Proxy Session Details
                      </p>
                      <ul className="mt-1 text-warning space-y-1">
                        <li>• Duration: 25 minutes with auto-timeout</li>
                        <li>• All actions will be logged for audit</li>
                        <li>• Limited to target user's permissions</li>
                        <li>• Session can be terminated at any time</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setSelectedUser(null)}>
                  Back
                </Button>
                <Button 
                  onClick={handleStartProxy}
                  disabled={isStarting}
                  className="bg-warning hover:bg-warning/90 text-warning-foreground"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    'Start Proxy Session'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}